// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { TextQuestion } from "./QuestionnaireTypes";
import diff from "fast-diff";

export const getDefaultQuestionDefinition = (): TextQuestion => {
  return {
    error: "",
    name: "default",
    label: "default",
    type: "text",
    formatter: val => val,
    mandatory: false,
    placeholder: "",
    validators: [],
  };
};

export const getCursorPosition = (params: {
  // Value before formatting.
  nonFormattedValue: string;
  // Cursor position in the nonFormattedValue
  cursorPosition: number;
  // Value after formatting.
  formattedValue: string;
}): number => {
  const { nonFormattedValue, formattedValue, cursorPosition } = params;

  if (cursorPosition === nonFormattedValue.length) {
    // Cursor was at the end, so just keep it at the end.
    return formattedValue.length;
  } else if (nonFormattedValue.length === formattedValue.length) {
    if (nonFormattedValue.toLowerCase() === formattedValue.toLowerCase()) {
      return cursorPosition;
    }

    const diffResult = diff(nonFormattedValue, formattedValue);
    let updatedCursorPosition = cursorPosition;

    let currentRun = 0;
    let item: diff.Diff | undefined;
    // We will look to see if the block right before the cursor was deleted and
    // possibly moved to another location, where we'll set the new cursor position.
    for (let i = 0; i < diffResult.length; i++) {
      item = diffResult[i];
      const type = item[0];
      const substring = item[1];
      if (type === diff.EQUAL || type === diff.DELETE) {
        currentRun += substring.length;
      }
      if (currentRun >= cursorPosition) {
        break;
      }
    }

    if (item && item[0] === diff.DELETE) {
      // The block right before the cursor was deleted, and probably moved.
      // Let's search for an insert of the same string, and move the cursor to its end.
      let newIndex = 0;
      const searchString = item[1];
      for (let i = 0; i < diffResult.length; i++) {
        const item = diffResult[i];
        const type = item[0];
        const substring = item[1];
        if (type === diff.EQUAL || type === diff.INSERT) {
          newIndex += substring.length;
        }
        if (type === diff.INSERT && substring === searchString) {
          updatedCursorPosition = newIndex;
          break;
        }
      }
    }
    return updatedCursorPosition;
  } else {
    // If the length is different, we will find the diff the before/after formatting.
    // Then we will iterate over each diff elements, and substract or add a delta for all
    // strings before the cursor index that were inserted or removed.
    const diffResult = diff(nonFormattedValue, formattedValue);

    let currentRun = 0;
    let delta = 0;

    for (let i = 0; i < diffResult.length; i++) {
      const item = diffResult[i];
      const type = item[0];
      const substring = item[1];
      if (type === diff.EQUAL) {
        currentRun += substring.length;
        if (currentRun >= cursorPosition) {
          break;
        }
        continue;
      }
      if (type === diff.INSERT) {
        delta += substring.length;
        continue;
      } else {
        // type === diff.DELETE
        currentRun += substring.length;
        delta -= substring.length;
        if (currentRun >= cursorPosition) {
          break;
        }
      }
    }
    return cursorPosition + delta;
  }
};
